import React, { useEffect, useState } from "react";
import {
  Table, Button, Modal, Form, Input,
  Switch, Space, message, Tag, Popconfirm,
  Tooltip, Empty, Row, Col, Divider
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, EyeOutlined, SearchOutlined,
  CheckCircleFilled, CloseCircleFilled,
  ExclamationCircleOutlined,
  AppstoreOutlined, EyeInvisibleOutlined,
  InfoCircleOutlined, DownOutlined, UpOutlined,
  BookOutlined
} from "@ant-design/icons";
import {
  getAdminReligionOptions,
  addReligionOption,
  updateReligionOption,
  toggleReligionOption,
  deleteReligionOption
} from "../../../../Api/CoreApi";
import "./ReligionSettings.css";

function ReligionSettings() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const [emojiPreview, setEmojiPreview] = useState("🔯");

  // 📱 Mobile View States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getAdminReligionOptions();
      setData(res.religions || []);
    } catch {
      message.error("Failed to load religions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ============================================
  // ✅ FIX: Form Disconnect Warning Logic
  // ============================================
  useEffect(() => {
    if (!open) return;

    if (editing) {
      form.setFieldsValue({
        label: editing.label,
        value: editing.value,
        emoji: editing.emoji,
        sort_order: editing.sort_order ?? 0,
      });
      setEmojiPreview(editing.emoji || "🔯");
    } else {
      form.resetFields();
      form.setFieldsValue({ sort_order: 0 });
      setEmojiPreview("🔯");
    }
  }, [open, editing, form]);

  const stats = {
    total: data.length,
    active: data.filter(r => r.is_active).length,
    inactive: data.filter(r => !r.is_active).length,
  };

  const filteredData = data.filter(item => {
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return (
      (item.label || "").toLowerCase().includes(s) ||
      (item.value || "").toLowerCase().includes(s)
    );
  });

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await updateReligionOption(editing.id, values);
        message.success(`"${values.label}" updated`);
      } else {
        await addReligionOption(values);
        message.success(`"${values.label}" added`);
      }
      setOpen(false);
      setEditing(null);
      loadData();
    } catch (err) {
      message.error(err?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (record) => {
    try {
      await toggleReligionOption(record.id);
      message.success(
        record.is_active
          ? `"${record.label}" hidden`
          : `"${record.label}" visible`
      );
      setData(prev =>
        prev.map(item =>
          item.id === record.id
            ? { ...item, is_active: !record.is_active }
            : item
        )
      );
    } catch {
      message.error("Toggle failed");
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteReligionOption(record.id);
      message.success(`"${record.label}" deleted`);
      setData(prev => prev.filter(item => item.id !== record.id));
    } catch {
      message.error("Delete failed");
    }
  };

  const openEdit = (record) => {
    setEditing(record);
    setOpen(true);
  };

  const openAdd = () => {
    setEditing(null);
    setOpen(true);
  };

  // ============================================
  // DESKTOP TABLE COLUMNS
  // ============================================
  const columns = [
    {
      title: "#", dataIndex: "sort_order", width: 60, align: "center",
      sorter: (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      render: (val) => (
        <span className="order-badge">{val || 0}</span>
      ),
    },
    {
      title: "Religion",
      render: (_, record) => (
        <div className="religion-cell">
          <div className={`religion-emoji-box ${record.is_active ? '' : 'disabled'}`}>
            {record.emoji || "🔯"}
          </div>
          <div className="religion-info">
            <span className={`religion-label ${record.is_active ? '' : 'disabled'}`}>
              {record.label}
            </span>
            <span className="religion-value-text">{record.value}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Value", dataIndex: "value", width: 140, responsive: ["lg"],
      render: (val) => (
        <span className="tech-value">{val}</span>
      ),
    },
    {
      title: "Status", width: 100, align: "center",
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.is_active}
          onChange={() => handleToggle(record)}
          checkedChildren={<CheckCircleFilled />}
          unCheckedChildren={<CloseCircleFilled />}
        />
      ),
    },
    {
      title: "Actions", width: 100, align: "center",
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />}
              className="rs-action-btn edit"
              onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title={`Delete "${record.label}"?`}
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete" okType="danger" cancelText="No"
            icon={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
          >
            <Tooltip title="Delete">
              <Button type="text" size="small" icon={<DeleteOutlined />}
                className="rs-action-btn delete" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ============================================
  // MOBILE CARD RENDERER
  // ============================================
  const renderMobileCard = (record) => (
    <div key={record.id} className={`religion-mobile-card ${!record.is_active ? 'disabled' : ''}`}>
      <div className="rmc-left">
        <div className={`rmc-emoji ${record.is_active ? '' : 'off'}`}>
          {record.emoji || "🔯"}
        </div>
        <div className="rmc-info">
          <h4>{record.label}</h4>
          <span className="rmc-value">{record.value}</span>
          <span className="rmc-order">
            <AppstoreOutlined /> Order: <b>{record.sort_order || 0}</b>
          </span>
        </div>
      </div>

      <div className="rmc-right">
        <Switch
          size="small"
          checked={record.is_active}
          onChange={() => handleToggle(record)}
        />
        <div className="rmc-actions">
          <button className="rmc-btn edit" onClick={() => openEdit(record)}>
            <EditOutlined />
          </button>
          <Popconfirm
            title={`Delete "${record.label}"?`}
            onConfirm={() => handleDelete(record)}
            okText="Delete" okType="danger" cancelText="No"
          >
            <button className="rmc-btn delete">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      </div>
    </div>
  );

  return (
    <div className="religion-page">

      {/* ============ HEADER + STATS ============ */}
      <div className="religion-header-wrapper">
        <div className="religion-page-header">
          <div className="religion-header-left">
            <div className="religion-title-icon">🕉️</div>
            <div className="religion-title-content">
              <h2 className="religion-page-title">Religion Settings</h2>
              <p className="religion-page-subtitle">
                Manage registration form religion options
              </p>
            </div>
          </div>

          <div className="religion-header-actions">
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={loadData}
                loading={loading}
                className="rs-refresh-btn icon-mobile-btn"
              >
                <span className="hide-on-mobile">Refresh</span>
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAdd}
              className="rs-add-btn icon-mobile-btn"
            >
              <span className="hide-on-mobile">Add Religion</span>
            </Button>
            {isMobile && (
              <Button
                className="rs-toggle-stats-btn icon-mobile-btn"
                onClick={() => setShowStats(!showStats)}
                icon={showStats ? <UpOutlined /> : <DownOutlined />}
              />
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className={`rs-stats-grid ${!isMobile || showStats ? 'show' : 'hide'}`}>
          <div className="rs-stat-item total">
            <div className="rs-stat-icon"><AppstoreOutlined /></div>
            <div className="rs-stat-text">
              <b>{stats.total}</b>
              <span>TOTAL</span>
            </div>
          </div>
          <div className="rs-stat-item visible">
            <div className="rs-stat-icon"><EyeOutlined /></div>
            <div className="rs-stat-text">
              <b>{stats.active}</b>
              <span>VISIBLE</span>
            </div>
          </div>
          <div className="rs-stat-item hidden">
            <div className="rs-stat-icon"><EyeInvisibleOutlined /></div>
            <div className="rs-stat-text">
              <b>{stats.inactive}</b>
              <span>HIDDEN</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============ PREVIEW ============ */}
      <div className="preview-card">
        <div className="preview-header">
          <BookOutlined />
          <h4>Form Preview</h4>
          <span className="preview-count">
            {data.filter(r => r.is_active).length} active
          </span>
        </div>
        <div className="preview-tags">
          {data.filter(r => r.is_active).length === 0 ? (
            <span className="no-active-text">
              ⚠️ No active religions — Users will see empty dropdown
            </span>
          ) : (
            data
              .filter(r => r.is_active)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              .map(r => (
                <span key={r.id} className="preview-tag">
                  {r.emoji} {r.label}
                </span>
              ))
          )}
        </div>
        <p className="preview-note">
          👀 Users will see these options in the registration form
        </p>
      </div>

      {/* ============ SEARCH + TABLE / MOBILE CARDS ============ */}
      <div className="religion-table-card">
        <div className="search-bar-section">
          <Input
            prefix={<SearchOutlined className="search-icon-color" />}
            placeholder="Search religions..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            className="rs-search-input"
          />
          <span className="results-count">
            <b>{filteredData.length}</b> / {data.length}
          </span>
        </div>

        {isMobile ? (
          <div className="religion-mobile-list">
            {filteredData.length === 0 ? (
              <div className="empty-mobile">
                <BookOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                <p>{searchText ? `No match for "${searchText}"` : "No religions yet"}</p>
                {!searchText && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
                    className="rs-add-btn">
                    Add First Religion
                  </Button>
                )}
              </div>
            ) : (
              filteredData.map(renderMobileCard)
            )}
          </div>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            pagination={false}
            size="middle"
            rowClassName={(record) => (!record.is_active ? "row-inactive-dim" : "")}
            className="religion-table"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  styles={{ image: { height: 60 } }} // ✅ Fix: imageStyle to styles={{ image }}
                  description={
                    <span style={{ color: '#94a3b8' }}>
                      {searchText ? `No match for "${searchText}"` : "No religions yet"}
                    </span>
                  }
                >
                  {!searchText && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
                      className="rs-add-btn">
                      Add First Religion
                    </Button>
                  )}
                </Empty>
              ),
            }}
          />
        )}
      </div>

      {/* ============ MODAL ============ */}
      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        footer={null}
        width={isMobile ? '95%' : 480}
        centered
        forceRender // ✅ FIX: Prevents form unmount warning
        title={
          <div className="rs-modal-title">
            <div className="rs-modal-icon">{editing ? "✏️" : "➕"}</div>
            <div>
              <h4>{editing ? "Edit Religion" : "Add New Religion"}</h4>
              <p>{editing ? `Editing "${editing?.label || ''}"` : "New option for registration form"}</p>
            </div>
          </div>
        }
      >
        <div className="emoji-preview-circle">
          {emojiPreview}
        </div>

        {editing && (
          <div className="edit-warning-banner">
            <InfoCircleOutlined />
            <span>
              Changing <strong>Value</strong> may break caste mapping. Prefer editing only Label & Emoji.
            </span>
          </div>
        )}

        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
          <Form.Item name="label" label="Religion Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Hindu" size="large" />
          </Form.Item>

          <Form.Item name="value" label="Value (Technical)" rules={[{ required: true, message: "Required" }]}
            extra={<span className="form-extra-note">Used for caste mapping — keep stable</span>}>
            <Input placeholder="e.g. Hindu" size="large" style={{ fontFamily: "monospace" }} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="emoji" label="Emoji">
                <Input
                  placeholder="🕉️" maxLength={5} size="large"
                  className="emoji-input"
                  onChange={(e) => setEmojiPreview(e.target.value || "🔯")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sort_order" label="Sort Order" initialValue={0}>
                <Input type="number" min={0} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }} />

          <div className="modal-btn-row">
            <Button
              size="large"
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              type="primary"
              loading={saving}
              size="large"
              className="rs-modal-save-btn"
            >
              {editing ? "Update Religion" : "Add Religion"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default ReligionSettings;